import { memo, useCallback, useMemo } from "react";
import { IEnrichedActivityFeedItem } from "@/activity-feed/activity-feed.d";
import { getRecordTypeForEvent } from "@/activity-feed/query";
import { SPIFF_RULE_EVENTS } from "@/spiff-rules/constants";
import { ClosedReferralDetailCard } from "./closed-referral-detail-card";
import { CustomerDetailCard } from "./customer-detail-card";
import { DefaultDetailCard } from "./default-detail-card";
import { NewReferralDetailCard } from "./new-referral-detail-card";
import { PricebookItemDetailCard } from "./pricebook-item-detail-card";
import { ReviewDetailCard } from "./review-detail-card";
import { YardSignPhotoDetailCard } from "./yard-sign-photo-detail-card";

interface ActivityDetailCardProps {
  activity: IEnrichedActivityFeedItem;
  inView: boolean;
  onOpenEventModal?: (activity: IEnrichedActivityFeedItem) => void;
}

export const ActivityDetailCard = memo(
  ({ activity, inView, onOpenEventModal }: ActivityDetailCardProps) => {
    const recordType = useMemo(
      () => getRecordTypeForEvent(activity.event_name),
      [activity.event_name],
    );

    const handleOpenEventModal = useCallback(() => {
      onOpenEventModal?.(activity);
    }, [activity, onOpenEventModal]);

    const displayName = useMemo(
      () => activity.details_json.display_identification,
      [activity.details_json.display_identification],
    );

    // Render the appropriate detail card based on event type
    if (activity.spiff_event_name === SPIFF_RULE_EVENTS.signup) {
      return (
        <CustomerDetailCard
          customerId={activity.underlying_record_id}
          inView={inView}
        />
      );
    }

    if (activity.spiff_event_name === SPIFF_RULE_EVENTS.referral_creation) {
      return (
        <NewReferralDetailCard
          referralId={activity.underlying_record_id}
          inView={inView}
        />
      );
    }

    if (activity.spiff_event_name === SPIFF_RULE_EVENTS.referral_completion) {
      return (
        <ClosedReferralDetailCard
          referralId={activity.underlying_record_id}
          inView={inView}
        />
      );
    }

    if (activity.spiff_event_name === SPIFF_RULE_EVENTS.five_star_review) {
      return (
        <ReviewDetailCard
          reviewId={activity.underlying_record_id}
          inView={inView}
          onOpenEventModal={handleOpenEventModal}
        />
      );
    }

    if (activity.spiff_event_name === SPIFF_RULE_EVENTS.yard_sign_photo) {
      return (
        <YardSignPhotoDetailCard
          photoId={activity.underlying_record_id}
          inView={inView}
        />
      );
    }

    if (activity.spiff_event_name === SPIFF_RULE_EVENTS.pricebook_item_sale) {
      return (
        <PricebookItemDetailCard
          invoiceItemId={activity.underlying_record_id}
          inView={inView}
        />
      );
    }

    // Default case - only render if no specific record type was matched
    if (!recordType) {
      return (
        <DefaultDetailCard
          recordType={activity.underlying_record_type}
          displayName={displayName}
        />
      );
    }

    return null;
  },
);

ActivityDetailCard.displayName = "ActivityDetailCard";
